/**
 * Leviathan Web - Accounts Payable Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface Bill {
  id: string;
  billNumber: string;
  status: string;
  billDate: string;
  dueDate: string;
  vendorInvoiceNumber: string | null;
  total: number;
  amountPaid: number;
  amountDue: number;
  supplier: {
    id: string;
    name: string;
    supplierNumber: string;
  };
  receipt: {
    id: string;
    receiptNumber: string;
  } | null;
  payments: Array<{
    id: string;
    amount: number;
    paymentDate: string;
  }>;
}

interface Stats {
  total: number;
  draft: number;
  pending: number;
  approved: number;
  overdue: number;
  paid: number;
  totalOutstanding: number;
  totalOverdue: number;
  paidThisMonth: number;
}

const AccountsPayablePage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [bills, setBills] = useState<Bill[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showOverdue, setShowOverdue] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchBills();
  }, [search, statusFilter, showOverdue]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bills/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchBills = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (showOverdue) params.append('overdue', 'true');

      const response = await fetch(`${API_URL}/api/bills?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBills(data.bills);
      }
    } catch (error) {
      console.error('Failed to fetch bills:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const isOverdue = (bill: Bill) => {
    if (['PAID', 'VOID', 'DRAFT', 'PENDING'].includes(bill.status)) return false;
    return new Date(bill.dueDate) < new Date();
  };

  const getDaysOverdue = (dueDate: string) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'DRAFT':
        return 'text-text-tertiary bg-text-tertiary/20 border-text-tertiary/30';
      case 'PENDING':
        return 'text-steel bg-steel/20 border-steel/30';
      case 'APPROVED':
        return 'text-amber bg-amber/20 border-amber/30';
      case 'SCHEDULED':
        return 'text-amber bg-amber/20 border-amber/30';
      case 'PARTIAL':
        return 'text-warning-amber bg-warning-amber/20 border-warning-amber/30';
      case 'PAID':
        return 'text-success-green bg-success-green/20 border-success-green/30';
      case 'VOID':
        return 'text-text-tertiary bg-text-tertiary/20 border-text-tertiary/30';
      default:
        return 'text-text-secondary bg-text-secondary/20 border-text-secondary/30';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Accounts Payable
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Vendor Bills & Payments
              </p>
            </div>

            <Link
              to="/ap/new"
              className="px-8 py-3 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Bill</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard label="Total Bills" value={stats.total.toString()} />
          <StatCard label="Outstanding" value={formatCurrency(stats.totalOutstanding)} color="amber" />
          <StatCard
            label="Overdue"
            value={formatCurrency(stats.totalOverdue)}
            color={stats.totalOverdue > 0 ? 'red' : 'green'}
          />
          <StatCard
            label="Overdue Count"
            value={stats.overdue.toString()}
            color={stats.overdue > 0 ? 'red' : 'green'}
          />
          <StatCard label="Paid" value={stats.paid.toString()} color="green" />
          <StatCard label="Paid (Month)" value={formatCurrency(stats.paidThisMonth)} color="steel" />
        </div>
      )}

      {/* Filters */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Search
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Bill #, vendor invoice, supplier..."
                className="form-input"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
              >
                <option value="">All Statuses</option>
                <option value="DRAFT">Draft</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="SCHEDULED">Scheduled</option>
                <option value="PARTIAL">Partial</option>
                <option value="PAID">Paid</option>
                <option value="VOID">Void</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setShowOverdue(!showOverdue)}
                className={`w-full font-mono py-2 px-4 transition-all uppercase text-sm tracking-wider border ${
                  showOverdue
                    ? 'bg-error-red/20 text-error-red border-error-red/50'
                    : 'bg-panel-border hover:bg-steel/20 text-text-primary border-panel-border'
                }`}
              >
                {showOverdue ? 'Showing Overdue' : 'Show Overdue'}
              </button>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearch('');
                  setStatusFilter('');
                  setShowOverdue(false);
                }}
                className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-sm tracking-wider border border-panel-border"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bills Table */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono animate-pulse">
                &gt; Loading bills...
              </div>
            </div>
          ) : bills.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono mb-2">&gt; No bills found</div>
              <div className="text-text-tertiary font-mono text-sm">
                Record vendor bills from receipts
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Bill
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Supplier
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Dates
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Total
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Balance
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Status
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((bill) => {
                    const overdue = isOverdue(bill);
                    const daysOver = getDaysOverdue(bill.dueDate);

                    return (
                      <tr
                        key={bill.id}
                        className={`border-b border-panel-border hover:bg-amber/5 transition-colors cursor-pointer ${
                          overdue ? 'bg-error-red/5' : ''
                        }`}
                        onClick={() => navigate(`/ap/${bill.id}`)}
                      >
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-amber font-mono font-semibold">
                              {bill.billNumber}
                            </div>
                            {bill.vendorInvoiceNumber && (
                              <div className="text-text-tertiary font-mono text-xs">
                                Vendor: {bill.vendorInvoiceNumber}
                              </div>
                            )}
                            {bill.receipt && (
                              <div className="text-text-tertiary font-mono text-xs">
                                Receipt: {bill.receipt.receiptNumber}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-text-primary font-mono">{bill.supplier.name}</div>
                            <div className="text-text-tertiary font-mono text-xs">
                              {bill.supplier.supplierNumber}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-text-secondary font-mono text-sm">
                            {formatDate(bill.billDate)}
                          </div>
                          <div
                            className={`font-mono text-xs ${
                              overdue ? 'text-error-red font-semibold' : 'text-text-tertiary'
                            }`}
                          >
                            Due: {formatDate(bill.dueDate)}
                            {overdue && ` (${daysOver}d overdue)`}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-text-primary font-mono font-semibold">
                            {formatCurrency(bill.total)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div
                            className={`font-mono font-semibold ${
                              bill.amountDue > 0 ? 'text-error-red' : 'text-success-green'
                            }`}
                          >
                            {formatCurrency(bill.amountDue)}
                          </div>
                          {bill.amountPaid > 0 && bill.amountDue > 0 && (
                            <div className="text-text-tertiary font-mono text-xs">
                              Paid: {formatCurrency(bill.amountPaid)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`px-3 py-1 font-mono text-xs uppercase tracking-wider border ${getStatusColor(bill.status)}`}
                          >
                            {bill.status}
                          </span>
                        </td>
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center space-x-2">
                            {bill.amountDue > 0 && !['VOID', 'DRAFT'].includes(bill.status) && (
                              <button
                                onClick={() => navigate(`/ap/${bill.id}?action=payment`)}
                                className="text-success-green hover:text-success-green/80 font-mono text-sm"
                                title="Record Payment"
                              >
                                💵
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/ap/${bill.id}/edit`)}
                              className="text-text-secondary hover:text-amber font-mono text-sm"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => navigate(`/ap/${bill.id}`)}
                              className="text-steel hover:text-amber font-mono text-sm"
                              title="View"
                            >
                              👁️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Stat card component
 */
function StatCard({
  label,
  value,
  color = 'amber',
}: {
  label: string;
  value: string;
  color?: 'amber' | 'green' | 'red' | 'steel';
}) {
  const colorClasses = {
    amber: 'text-amber',
    green: 'text-success-green',
    red: 'text-error-red',
    steel: 'text-steel',
  };

  const glowColors = {
    amber: 'rgba(255, 149, 0, 0.6)',
    green: 'rgba(34, 197, 94, 0.6)',
    red: 'rgba(220, 38, 38, 0.6)',
    steel: 'rgba(92, 140, 199, 0.6)',
  };

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 text-center">
        <div className="text-text-tertiary font-mono text-xs uppercase tracking-wider mb-1">
          {label}
        </div>
        <div
          className={`text-xl font-mono font-bold ${colorClasses[color]}`}
          style={{ textShadow: `0 0 8px ${glowColors[color]}` }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export default AccountsPayablePage;
